/**
 * ApprovalWorkflow-GraphRenderer — render a workflow as a graphviz SVG and make it interactive.
 *
 * This replaces the pict-section-flow canvas: graphviz (WASM) does the entire diagram — layout, spline edge
 * routing with proper attachment + arrowheads, non-overlapping labels, crossing minimisation. We inject its
 * SVG, add mouse pan/zoom (by mutating the SVG viewBox), and delegate clicks on graphviz's `.node` / `.edge`
 * groups back to the model via the id maps from ApprovalWorkflow-Dot.
 *
 * One renderer instance per editor; `render(workflow)` re-runs graphviz and re-wires. graphviz loads once
 * (WASM inlined in the package, so it bundles — no .wasm to serve).
 */

const libDot = require('./ApprovalWorkflow-Dot.js');

// Pointer travel (client px) between press and release below which the gesture is a CLICK, not a pan.
// A real mouse/trackpad click almost always jitters a few pixels; too tight a value here swallows the
// click (it looks like a tiny drag) so nodes/edges never open their panel. Pan begins past this.
const DRAG_THRESHOLD = 6;

let _graphvizPromise = null;

function _loadGraphviz()
{
	if (!_graphvizPromise)
	{
		// Required lazily so the (large) WASM module only initialises when a graph is actually shown.
		//
		// We pull in the graphviz build DIRECTLY by its file path rather than the package entry. The umbrella
		// entries (@hpcc-js/wasm main/browser) are thin wrappers whose .load() does a runtime `import('./graphviz.js')`
		// (or bare `require('./graphviz.cjs')`) to fetch the real module — browserify can't resolve those, so the
		// bundled code throws "require is not defined" at render time. dist/graphviz.cjs is the whole graphviz module
		// with the WASM inlined as base64 and browser instantiation (WebAssembly.instantiate): no dynamic import, no
		// bare require, so browserify bundles it cleanly. (browserify 17 ignores the package `exports` map, so the
		// explicit dist path resolves; this file is browser-only, so node's exports enforcement never applies.)
		const libGraphviz = require('@hpcc-js/wasm/dist/graphviz.cjs');
		_graphvizPromise = libGraphviz.Graphviz.load();
	}
	return _graphvizPromise;
}

class ApprovalGraphRenderer
{
	/**
	 * @param {HTMLElement} pContainer  the element the SVG is injected into
	 * @param {Object} pCallbacks       { onNodeClick(status), onEdgeClick({SourceStatus,TransitionIndex,IsBranch,ToStatus}) }
	 */
	constructor(pContainer, pCallbacks)
	{
		this.container = pContainer;
		this.callbacks = pCallbacks || {};
		this._nodeMap = {};
		this._edgeMap = {};
		this._statusOrder = null; // states in the laid-out top-to-bottom order (for the steps rail)
		this._svg = null;
		this._view = null;      // current viewBox { x, y, w, h }
		this._baseView = null;  // graphviz's natural bounds (for fit)
		this._panning = false;
		this._didPan = false;
		this._panStart = null;
		this._renderSeq = 0;

		// Panning tracks the pointer across the whole window (so a fast drag off the SVG still works). Bound
		// once; they read the current _svg/_view, so they survive re-renders.
		this._boundMove = (pEvent) => this._onPointerMove(pEvent);
		this._boundUp = (pEvent) => this._onPointerUp(pEvent);
		if (typeof window !== 'undefined')
		{
			window.addEventListener('pointermove', this._boundMove);
			window.addEventListener('pointerup', this._boundUp);
		}
	}

	/** Render the workflow. Options: { keepView } preserves the current pan/zoom across a re-render. */
	async render(pWorkflow, pOptions)
	{
		const tmpOptions = pOptions || {};
		const tmpBuilt = libDot.blobToDot(pWorkflow);
		this._nodeMap = tmpBuilt.NodeMap;
		this._edgeMap = tmpBuilt.EdgeMap;

		const tmpSeq = ++this._renderSeq;
		const tmpGraphviz = await _loadGraphviz();
		if (tmpSeq !== this._renderSeq) { return; } // a newer render superseded this one

		let tmpSvgText;
		try { tmpSvgText = tmpGraphviz.dot(tmpBuilt.Dot); }
		catch (pError) { this.container.innerHTML = `<div class="apw-graph-error">Could not render the workflow diagram.</div>`; return; }

		this.container.innerHTML = tmpSvgText;
		const tmpSvg = this.container.querySelector('svg');
		if (!tmpSvg) { return; }
		this._prepareSvg(tmpSvg, !!tmpOptions.keepView);
		this._wireSvg(tmpSvg);
		this._computeStatusOrder(tmpSvg);
		if (typeof this.callbacks.onRendered === 'function') { this.callbacks.onRendered(); }
	}

	/**
	 * Record the states in the order graphviz laid them out, top to bottom (each `.node` group's vertical
	 * position). The steps rail reads this so it lists states in the SAME order the diagram shows — which, for
	 * a cyclic workflow, graphviz derives from the graph, not the stored array order.
	 */
	_computeStatusOrder(pSvg)
	{
		const tmpNodes = [];
		pSvg.querySelectorAll('.node').forEach((pEl) =>
		{
			const tmpStatus = this._nodeMap[pEl.getAttribute('id')];
			if (tmpStatus == null) { return; }
			let tmpY = 0;
			try { tmpY = pEl.getBBox().y; } catch (pError) { tmpY = 0; }
			tmpNodes.push({ Status: tmpStatus, Y: tmpY });
		});
		tmpNodes.sort((pA, pB) => pA.Y - pB.Y);
		this._statusOrder = tmpNodes.map((pNode) => pNode.Status);
	}

	/** The states in diagram (top-to-bottom) order, or null before the first successful render. */
	getStatusOrder()
	{
		return this._statusOrder ? this._statusOrder.slice() : null;
	}

	/** Make the SVG fill the container and take over its viewBox for pan/zoom. */
	_prepareSvg(pSvg, pKeepView)
	{
		const tmpViewBox = (pSvg.getAttribute('viewBox') || '0 0 100 100').split(/\s+/).map(parseFloat);
		this._baseView = { x: tmpViewBox[0], y: tmpViewBox[1], w: tmpViewBox[2], h: tmpViewBox[3] };
		if (!pKeepView || !this._view)
		{
			this._view = Object.assign({}, this._baseView);
		}
		pSvg.removeAttribute('width');
		pSvg.removeAttribute('height');
		pSvg.setAttribute('width', '100%');
		pSvg.setAttribute('height', '100%');
		pSvg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
		pSvg.style.display = 'block';
		pSvg.style.cursor = 'grab';
		pSvg.style.touchAction = 'none';
		this._svg = pSvg;
		this._applyView();
	}

	_applyView()
	{
		if (!this._svg || !this._view) { return; }
		const tmpV = this._view;
		this._svg.setAttribute('viewBox', `${tmpV.x} ${tmpV.y} ${tmpV.w} ${tmpV.h}`);
	}

	/** Reset pan/zoom to fit the whole diagram. */
	fit()
	{
		if (this._baseView)
		{
			this._view = Object.assign({}, this._baseView);
			this._applyView();
		}
	}

	/** Zoom by a factor around the diagram centre (for the on-canvas +/- buttons). <1 zooms in. */
	zoomBy(pFactor)
	{
		if (!this._svg || !this._view) { return; }
		const tmpV = this._view;
		const tmpCX = tmpV.x + (tmpV.w / 2);
		const tmpCY = tmpV.y + (tmpV.h / 2);
		tmpV.w *= pFactor;
		tmpV.h *= pFactor;
		tmpV.x = tmpCX - (tmpV.w / 2);
		tmpV.y = tmpCY - (tmpV.h / 2);
		this._applyView();
	}

	_wireSvg(pSvg)
	{
		pSvg.addEventListener('click', (pEvent) => this._onClick(pEvent));
		pSvg.addEventListener('wheel', (pEvent) => { pEvent.preventDefault(); this._onWheel(pEvent); }, { passive: false });
		pSvg.addEventListener('pointerdown', (pEvent) => this._onPointerDown(pEvent));
	}

	_onClick(pEvent)
	{
		// A drag-pan ends in a click; ignore it so panning doesn't open panels.
		if (this._didPan) { this._didPan = false; return; }
		const tmpGroup = pEvent.target && pEvent.target.closest ? pEvent.target.closest('.node, .edge') : null;
		if (!tmpGroup) { return; }
		const tmpId = tmpGroup.getAttribute('id');
		if (!tmpId) { return; }
		if (this._nodeMap[tmpId] && typeof this.callbacks.onNodeClick === 'function')
		{
			this.callbacks.onNodeClick(this._nodeMap[tmpId]);
		}
		else if (this._edgeMap[tmpId] && typeof this.callbacks.onEdgeClick === 'function')
		{
			this.callbacks.onEdgeClick(this._edgeMap[tmpId]);
		}
	}

	/** Client pixel → SVG-coordinate point, using the current viewBox. */
	_toSvg(pClientX, pClientY)
	{
		const tmpRect = this._svg.getBoundingClientRect();
		const tmpV = this._view;
		return {
			x: tmpV.x + (((pClientX - tmpRect.left) / tmpRect.width) * tmpV.w),
			y: tmpV.y + (((pClientY - tmpRect.top) / tmpRect.height) * tmpV.h),
		};
	}

	_onWheel(pEvent)
	{
		if (!this._svg || !this._view) { return; }
		const tmpFactor = (pEvent.deltaY < 0) ? 0.85 : (1 / 0.85);
		const tmpPoint = this._toSvg(pEvent.clientX, pEvent.clientY);
		const tmpV = this._view;
		// Keep the point under the cursor fixed while scaling.
		tmpV.x = tmpPoint.x - ((tmpPoint.x - tmpV.x) * tmpFactor);
		tmpV.y = tmpPoint.y - ((tmpPoint.y - tmpV.y) * tmpFactor);
		tmpV.w *= tmpFactor;
		tmpV.h *= tmpFactor;
		this._applyView();
	}

	_onPointerDown(pEvent)
	{
		if (pEvent.button !== 0 || !this._view) { return; }
		this._panning = true;
		this._didPan = false;
		this._panStart = { X: pEvent.clientX, Y: pEvent.clientY, VX: this._view.x, VY: this._view.y };
		if (this._svg) { this._svg.style.cursor = 'grabbing'; }
	}

	_onPointerMove(pEvent)
	{
		if (!this._panning || !this._svg || !this._view) { return; }
		const tmpMoveX = pEvent.clientX - this._panStart.X;
		const tmpMoveY = pEvent.clientY - this._panStart.Y;
		// Until the pointer has travelled past DRAG_THRESHOLD this is still a (jittery) click, not a pan:
		// don't move the view and don't set _didPan, so _onClick still opens the node/edge panel.
		if (!this._didPan && (((tmpMoveX * tmpMoveX) + (tmpMoveY * tmpMoveY)) <= (DRAG_THRESHOLD * DRAG_THRESHOLD))) { return; }
		this._didPan = true;
		const tmpRect = this._svg.getBoundingClientRect();
		this._view.x = this._panStart.VX - ((tmpMoveX / tmpRect.width) * this._view.w);
		this._view.y = this._panStart.VY - ((tmpMoveY / tmpRect.height) * this._view.h);
		this._applyView();
	}

	_onPointerUp()
	{
		this._panning = false;
		if (this._svg) { this._svg.style.cursor = 'grab'; }
	}

	/** Outline the node for a given business Status (used by the steps rail). */
	highlightStatus(pStatus)
	{
		const tmpId = Object.keys(this._nodeMap).find((pKey) => this._nodeMap[pKey] === pStatus);
		this.highlight('node', tmpId || null);
	}

	/** Highlight a node/edge by id (adds a class graphviz elements can be styled by). */
	highlight(pKind, pRefMatch)
	{
		if (!this._svg) { return; }
		this._svg.querySelectorAll('.apw-selected').forEach((pEl) => pEl.classList.remove('apw-selected'));
		if (!pRefMatch) { return; }
		// pRefMatch is an id (s#/e#).
		const tmpEl = this._svg.querySelector(`#${pRefMatch}`);
		if (tmpEl) { tmpEl.classList.add('apw-selected'); }
	}

	destroy()
	{
		if (typeof window !== 'undefined')
		{
			window.removeEventListener('pointermove', this._boundMove);
			window.removeEventListener('pointerup', this._boundUp);
		}
	}
}

module.exports = ApprovalGraphRenderer;
